import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Search, Plus, Wrench, MapPin, ExternalLink, RefreshCw, Calendar } from 'lucide-react';
import { getServicios } from '../../lib/gestion4satApi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const tipoLabels = {
  instalacion: 'Instalaci\u00f3n', mantencion: 'Mantenci\u00f3n',
  asistencia: 'Asistencia', revision: 'Revisi\u00f3n',
  retiro: 'Retiro', otro: 'Otro',
};

const tipoColors = {
  instalacion: 'bg-green-500/10 text-green-600 border-green-500/20',
  mantencion: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  asistencia: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  revision: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  retiro: 'bg-red-500/10 text-red-600 border-red-500/20',
  otro: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const facturacionColors = {
  pendiente: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
  en_proceso: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  facturado: 'bg-green-500/10 text-green-600 border-green-500/20',
};

export default function G4sServiciosPage() {
  const [servicios, setServicios] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const d = await getServicios({
        q: search, tipo: filtroTipo, estado_facturacion: filtroEstado,
        fecha_desde: fechaDesde, fecha_hasta: fechaHasta, limit: '100',
      });
      setServicios(d.servicios);
      setTotal(d.total);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [search, filtroTipo, filtroEstado, fechaDesde, fechaHasta]);

  useEffect(() => { load(); }, [load]);

  const openMap = (coords) => {
    if (coords?.lat && coords?.lng) {
      window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, '_blank');
    }
  };

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-servicios-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Servicios en Terreno</h1>
            <p className="text-sm text-muted-foreground">{total} servicios registrados</p>
          </div>
          <Button onClick={() => navigate('/gestion4sat/servicios/nuevo')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-nuevo-servicio">
            <Plus className="h-4 w-4 mr-2" /> Nuevo Servicio
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="IMEI, patente, empresa, t\u00e9cnico..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-servicios" />
          </div>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(tipoLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Facturac." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="en_proceso">En Proceso</SelectItem>
              <SelectItem value="facturado">Facturado</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} className="w-36" placeholder="Desde" />
            <Input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} className="w-36" placeholder="Hasta" />
          </div>
        </div>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-semibold">Fecha</th>
                  <th className="text-left p-3 font-semibold">Tipo</th>
                  <th className="text-left p-3 font-semibold">Patente</th>
                  <th className="text-left p-3 font-semibold hidden md:table-cell">IMEI</th>
                  <th className="text-left p-3 font-semibold hidden lg:table-cell">Empresa</th>
                  <th className="text-left p-3 font-semibold hidden md:table-cell">T&eacute;cnico</th>
                  <th className="text-center p-3 font-semibold">Estado</th>
                  <th className="text-center p-3 font-semibold">GPS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b"><td colSpan={8} className="p-3"><Skeleton className="h-8 w-full" /></td></tr>
                  ))
                ) : servicios.length === 0 ? (
                  <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">
                    Sin servicios registrados. Haz click en Nuevo Servicio para crear uno.
                  </td></tr>
                ) : (
                  servicios.map(s => (
                    <tr key={s.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/gestion4sat/servicios/${s.id}`)}>
                      <td className="p-3 text-xs font-mono">{s.fecha_servicio}</td>
                      <td className="p-3">
                        <Badge className={tipoColors[s.tipo] || tipoColors.otro}>
                          {tipoLabels[s.tipo] || s.tipo}
                        </Badge>
                        {s.es_reasignacion && <Badge className="ml-1 bg-orange-500/10 text-orange-600 text-xs">Reasig.</Badge>}
                      </td>
                      <td className="p-3 font-mono font-medium">{s.patente_formateada || '-'}</td>
                      <td className="p-3 font-mono text-xs hidden md:table-cell">{s.imei || '-'}</td>
                      <td className="p-3 text-xs hidden lg:table-cell">{s.empresa_razon_social || '-'}</td>
                      <td className="p-3 text-xs hidden md:table-cell">{s.tecnico_nombre}</td>
                      <td className="p-3 text-center">
                        <Badge className={facturacionColors[s.estado_facturacion] || ''}>
                          {s.estado_facturacion}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        {s.coordenadas?.lat ? (
                          <button onClick={e => { e.stopPropagation(); openMap(s.coordenadas); }} className="text-blue-500 hover:text-blue-700" data-testid={`map-${s.id}`}>
                            <MapPin className="h-4 w-4" />
                          </button>
                        ) : <span className="text-muted-foreground text-xs">-</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </G4sLayout>
  );
}
